import { useRef, useEffect, useCallback, RefObject } from 'react';
import ThreeJSApp from '@/shared/lib/threejs/app';
import { useAvatarStore } from '@/shared/store/avatar-store';
import { useShallow } from 'zustand/react/shallow';

interface UseThreeAppOptions {
  canvasRef: RefObject<HTMLCanvasElement | null>;
}

interface UseThreeAppReturn {
  app: ThreeJSApp | null;
  isInitialized: boolean;
  destroy: () => void;
  resize: (width?: number, height?: number) => void;
}

export const useThreeApp = ({
  canvasRef,
}: UseThreeAppOptions): UseThreeAppReturn => {
  const appRef = useRef<ThreeJSApp | null>(null);
  const isInitializedRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  // Zustand store 액션들을 useShallow로 최적화
  const { updateProgress, setError, reset, setIsThreejsAppReady } = useAvatarStore(
    useShallow((state) => ({
      updateProgress: state.updateProgress,
      setError: state.setError,
      reset: state.reset,
      setIsThreejsAppReady: state.setIsThreejsAppReady,
    }))
  );

  // 안정적인 함수 참조들을 useMemo로 최적화
  const destroy = useCallback(() => {
    console.log('🎭 ThreeCanvas: ThreeJS 앱 정리 시작');
    
    // Resize timeout 정리
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }
    
    // ResizeObserver 정리
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    
    if (appRef.current && !appRef.current.isDestroyed()) {
      appRef.current.destroy();
      appRef.current = null;
    }
    isInitializedRef.current = false;
    // 스토어 상태도 리셋
    reset();
  }, [reset]);

  const resize = useCallback((width?: number, height?: number) => {
    if (appRef.current && !appRef.current.isDestroyed()) {
      appRef.current.resize(width, height);
    }
  }, []);

  const handleLoadProgress = useCallback((progressData: { progress: number }) => {
    updateProgress(progressData.progress);

  }, [updateProgress]);

  // Three.js 앱 초기화 (로딩 진행률 이벤트 리스닝)
  useEffect(() => {
    const canvas = canvasRef.current;

    console.log('🎭 [USE-THREE-APP] useEffect 실행:', {
      hasAppRef: !!appRef.current,
      hasCanvas: !!canvas,
      canvasSize: canvas ? { width: canvas.width, height: canvas.height } : null,
    });

    if (appRef.current || !canvas) {
      console.log('🎭 [USE-THREE-APP] 초기화 스킵:', appRef.current ? 'appRef 이미 존재' : 'canvas 없음');
      return;
    }

    const initThreeJS = async () => {
      try {
        if (!canvas) {
          throw new Error('Canvas element is not available');
        }

        console.log('🎭 [USE-THREE-APP] ThreeJSApp 생성 시작');
        const newApp = new ThreeJSApp({ canvas });
        appRef.current = newApp;
        console.log('🎭 [USE-THREE-APP] ThreeJSApp 생성 완료, setIsThreejsAppReady(true) 호출');
        setIsThreejsAppReady(true);
        isInitializedRef.current = true;

        // 로딩 진행률 이벤트 리스닝
        newApp.on('loadProgress', handleLoadProgress);

        // 초기 resize 호출
        setTimeout(() => {
          if (newApp && !newApp.isDestroyed()) {
            newApp.resize();
          }
        }, 0);

        // ResizeObserver로 canvas 크기 변화 감지 (debounce 적용)
        const RESIZE_DEBOUNCE_MS = 100; // 100ms debounce
        const SIZE_THRESHOLD = 1; // 1px 이상 변화 시에만 리사이즈
        
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === canvas && appRef.current && !appRef.current.isDestroyed()) {
              const { width, height } = entry.contentRect;
              
              // 유효한 크기인지 확인
              if (width <= 0 || height <= 0) continue;
              
              // 이전 크기와 비교하여 실질적인 변화가 있는지 확인
              const widthDiff = Math.abs(width - lastSizeRef.current.width);
              const heightDiff = Math.abs(height - lastSizeRef.current.height);
              
              if (widthDiff < SIZE_THRESHOLD && heightDiff < SIZE_THRESHOLD) continue;
              
              // debounce: 이전 타임아웃이 있으면 취소
              if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
              }
              
              // 새로운 타임아웃 설정
              resizeTimeoutRef.current = setTimeout(() => {
                if (appRef.current && !appRef.current.isDestroyed()) {
                  console.log('🎭 [USE-THREE-APP] Canvas 크기 변경 적용:', { width, height });
                  lastSizeRef.current = { width, height };
                  appRef.current.resize(width, height);
                }
              }, RESIZE_DEBOUNCE_MS);
            }
          }
        });
        
        // 초기 크기 저장
        const rect = canvas.getBoundingClientRect();
        lastSizeRef.current = { width: rect.width, height: rect.height };
        
        resizeObserverRef.current.observe(canvas);

      } catch (error) { //TODO: 에러 처리 필요  
        console.error('Three.js initialization failed:', error);
        setError((error as Error).message);

      }
    };

    initThreeJS();

    return () => {
      destroy();
    };
  }, [canvasRef, handleLoadProgress, setError, destroy]); //TODO: 에러 처리 필요
  return {
    app: appRef.current,
    isInitialized: isInitializedRef.current,
    destroy,
    resize,
  };
};
